/* @flow */
import cx from 'classnames';
import React from 'react';
import shallowCompare from 'react-addons-shallow-compare';

import focusNode from 'utils/focusNode';
import KeyConstants from 'constants/KeyConstants';
import Text from 'components/Text.jsx';
import Token from 'components/Token.jsx';

import styles from './styles/Tokenizer.css';

class Tokenizer extends React.Component {
  props: {
    value: Array<*>,
    prompt?: ?string,
    className?: ?string,
    inputClassName?: ?string,
    placeholder?: ?string,
    tokenRenderer: Function,
    autoFocus?: ?boolean,
    /* When the list of tokens changes */
    onChange: (data: *) => void,
    onDeselect: Function,
    /* When the typed input changes */
    onInputChange: Function,
    onInputKeyDown: Function,
    onFocus?: ?Function,
    onBlur?: ?Function,
  };

  static defaultProps: {
    value: Array<*>,
    onInputChange: Function,
  };

  static defaultProps = {
    value: [],
    tokenRenderer: data => <Token>{data}</Token>,
    onChange: (value) => {}, // eslint-disable-line
    onDeselect: () => {},
    onInputChange: () => {},
    // typecheck: ignore statement
    onInputKeyDown: (e: SyntheticKeyboardEvent) => {}, // eslint-disable-line
  };

  state = {
    inputValue: '',
    focused: false,
  };

  shouldComponentUpdate(nextProps: Object, nextState: Object) {
    return shallowCompare(this, nextProps, nextState);
  }

  getInput() {
    return this.refs.input;
  }

  getInputValue() {
    return this.state.inputValue;
  }

  clearInputValue() {
    this.setState({ inputValue: '' }, () => this.props.onInputChange(''));
  }

  focusInput() {
    focusNode(this.getInput());
  }

  // typecheck: ignore statement
  add(data: *|*[]) {
    const { value } = this.props;
    const newValue = Array.from(value);

    (Array.isArray(data) ? data : [data]).forEach(datum => {
      const index = value.indexOf(datum);
      if (index < 0) {
        newValue.push(datum);
      }
    });

    if (newValue.length !== value.length) {
      this.props.onChange(newValue);
    }
  }

  // typecheck: ignore statement
  remove(datum: *) {
    const { value } = this.props;
    const index = value.indexOf(datum);
    if (index >= 0) {
      const newValue = value.slice(0, index).concat(value.slice(index + 1));
      this.props.onChange(newValue);
      this.props.onDeselect(datum);
    }
  }

  handleBlur = () => {
    this.setState({ focused: false });
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  }

  handleFocus = () => {
    this.setState({ focused: true });
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  }

  // typecheck: ignore statement
  handleInputChange(event: SyntheticEvent) {
    const { target } = event;
    if (target instanceof window.HTMLInputElement) {
      const inputValue = target.value;
      this.setState({ inputValue });
      this.props.onInputChange(inputValue);
    }
  }

  // typecheck: ignore statement
  handleInputKeyDown(event: SyntheticKeyboardEvent) {
    const { keyCode } = event;
    switch (keyCode) {
      case KeyConstants.BACKSPACE:
        if (this.state.inputValue === '') {
          if (this.props.value.length > 0) {
            this.remove(this.props.value[this.props.value.length - 1]);
          }
        }
        break;
      default:
        break;
    }
    this.props.onInputKeyDown(event);
  }

  renderPrompt() {
    const { prompt } = this.props;
    if (!prompt) {
      return null;
    }
    return (
      <Text
        className={styles.prompt}
        color="pitch"
        weight="semibold">
        {prompt}
      </Text>
    );
  }

  renderTokens() {
    return this.props.value.map(this.renderToken.bind(this));
  }

  renderToken(datum: *, index: number, data: *[]) {
    const token = this.props.tokenRenderer(datum, index, data);
    return React.cloneElement(token, {
      key: index,
      onRemove: () => this.remove(datum),
    });
  }

  renderInput() {
    return (
      <div className={styles.inputContainer}>
        {!this.state.inputValue && this.renderPlaceholder()}
        <input
          ref="input"
          className={cx({
            [styles.inputHasValue]: !!this.state.inputValue || this.props.value.length > 0,
          }, this.props.inputClassName)}
          type="text"
          value={this.state.inputValue}
          size={this.state.inputValue.length || 1}
          autoFocus={this.props.autoFocus}
          onChange={event => this.handleInputChange(event)}
          onKeyDown={event => this.handleInputKeyDown(event)}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        />
      </div>
    );
  }

  renderPlaceholder() {
    const { placeholder } = this.props;
    if (!placeholder || this.props.value.length > 0) {
      return null;
    }
    return (
      <Text className={styles.placeholder} color="koala">
        {placeholder}
      </Text>
    );
  }

  render() {
    return (
      <div
        className={cx(styles.this, {
          [styles.focused]: this.state.focused,
        }, this.props.className)}
        onClick={() => this.focusInput()}>
        {this.renderPrompt()}
        {this.renderTokens()}
        {this.renderInput()}
      </div>
    );
  }
}

export default Tokenizer;



// WEBPACK FOOTER //
// ./src/components/Tokenizer.jsx